import { Injectable, signal } from '@angular/core';
import { IntakeOption, Organization, User } from '@ee/common/models';

/**
 * What the host filling the form knows and the live composites need: who is filling it, the form's
 * v1 options, and whether courts may be chosen.
 *
 * A service rather than inputs, because the address block sits three components below the wizard
 * (wizard → renderer → cell → block) and every host would otherwise thread five bindings through
 * components that have no use for them. A host that wants the attorney intake's behaviour declares
 * `providers: [IntakeHostContext]` and fills this in; one that provides nothing gets the root
 * instance, whose defaults draw the address exactly as the block did before any of this existed.
 *
 * The flags mirror what v1's property section (`eviction-property-section.component.ts`) read off its
 * inputs, with one translation: v1 told a logged-in attorney from an anonymous public filer by
 * `loggedInOrg`, and the v2 public link passes the attorney *as* the logged-in org. So the host states
 * both facts explicitly instead of the block guessing from the org type.
 */
@Injectable({ providedIn: 'root' })
export class IntakeHostContext {
  /** The intake form's v1 options (`options.property_county` and friends). Null when the host has none. */
  readonly options = signal<Record<string, IntakeOption> | null>(null);

  readonly loggedInOrg = signal<Organization | null>(null);

  readonly loggedInUser = signal<User | null>(null);

  /** An attorney filling the form for a client (v1's `attorneySelectsCourt`, the wizard's firm step). */
  readonly attorneyFilling = signal(false);

  /**
   * A client, or a public filer, filling the attorney's intake form — where v1's client-only product
   * rules apply (the notice date skipped behind a waiver). Off by default, so a host that provides
   * nothing — the PM case-create wizard, with its own template — keeps every question it placed.
   */
  readonly clientFilling = signal(false);

  /**
   * The public link, or any render with no logged-in organization. v1 showed the county, precinct and
   * place controls unconditionally there (its gate compared `loggedInOrg?.id` with an input that was
   * never set, which is true exactly when there is no org), so the public form always asks.
   */
  readonly publicLink = signal(true);

  /** v1's `allowCourtSelection`: null means "an attorney org may"; the attorney intake sets it. */
  readonly allowCourtSelection = signal<boolean | null>(false);

  /**
   * Whether precinct / township, place and court(s) are offered at all. On for the attorney intake,
   * where v1 asked them; off for hosts whose cases carry no venue beyond the county.
   */
  readonly jurisdiction = signal(false);

  /** The client the case is for, when one has been chosen: its county override rules and address fill. */
  readonly selectedClient = signal<Organization | null>(null);
}
